import { useMemo } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Plus } from 'lucide-react'
import { SettingsFieldDescription } from '@/components/settings/settings-field-copy'
import { SettingsRowFrame } from '@/components/settings/settings-row-frame'
import { SettingsSectionHeading } from '@/components/settings/settings-section-heading'
import { Button } from '@/components/ui/button'
import { RubricCompetencyCard } from '@/components/admin/evaluation-rubrics/rubric-competency-card'
import { RubricDimensionCard } from '@/components/admin/evaluation-rubrics/rubric-dimension-card'
import { RubricRequirementCard } from '@/components/admin/evaluation-rubrics/rubric-requirement-card'
import { WeightAllocationBar } from '@/components/admin/evaluation-rubrics/weight-allocation-bar'
import { type RubricCriterion } from '@/hooks/use-evaluation-rubrics-admin'
import {
  normalizeWeightsForKind,
  newCriterionDraft,
  scaleTypeOf,
  weightTotalForKind,
} from '@/lib/rubric-publish-payload'

type RubricCriteriaEditorProps = {
  criteria: RubricCriterion[]
  onChange: (criteria: RubricCriterion[]) => void
  validationMessage?: string | null
  skillOptions?: string[]
  sectionId?: string
  /** When set, only render these criterion kinds (for canvas editors). */
  kinds?: Array<RubricCriterion['kind']>
  /** Canvas editors omit settings panel chrome. */
  variant?: 'settings' | 'canvas'
  /**
   * full = name/definition/weights/reorder (legacy settings + older canvases).
   * l2 = canvas focused edit — levels/skills or stable keys; L1 lives on the case file.
   */
  depth?: 'full' | 'l2'
  /** When set, scroll/highlight this criterion key (focus canvas deep-link). */
  focusCriterionKey?: string
}

type RowErrors = Record<number, { label?: string; key?: string }>

const GROUPS: Array<{ kind: RubricCriterion['kind']; title: string; help: string }> = [
  {
    kind: 'dimension',
    title: 'Match dimensions',
    help: 'Core scored areas that make up the overall match. Weights should total 100%.',
  },
  {
    kind: 'competency',
    title: 'Focus areas',
    help: 'Competencies the AI should look for (often with named performance levels).',
  },
  {
    kind: 'requirement',
    title: 'Requirements',
    help: 'Pass/fail checks that must be met (or flagged when missing).',
  },
]

function rowErrorsFor(criteria: RubricCriterion[]): RowErrors {
  const keys = new Set<string>()
  const errors: RowErrors = {}
  criteria.forEach((row, index) => {
    const rowErr: { label?: string; key?: string } = {}
    if (!row.label.trim()) rowErr.label = 'Label is required'
    if (!row.key.trim()) rowErr.key = 'Key is required'
    else if (keys.has(row.key)) rowErr.key = 'Duplicate key'
    keys.add(row.key)
    if (Object.keys(rowErr).length) errors[index] = rowErr
  })
  return errors
}

function SortableKindCard({
  id,
  kind,
  row,
  errors,
  skillOptions,
  depth,
  onUpdate,
  onRemove,
}: {
  id: string
  kind: RubricCriterion['kind']
  row: RubricCriterion
  errors?: { label?: string; key?: string }
  skillOptions: string[]
  depth: 'full' | 'l2'
  onUpdate: (patch: Partial<RubricCriterion>) => void
  onRemove: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: depth === 'l2',
  })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  const chrome = {
    row,
    errors,
    dragHandleProps: { ...attributes, ...listeners },
    setNodeRef,
    style,
    isDragging,
    onRemove,
    hideDrag: depth === 'l2',
    hideRemove: depth === 'l2',
  }

  if (kind === 'competency') {
    return (
      <RubricCompetencyCard
        {...chrome}
        onUpdate={onUpdate}
        skillOptions={skillOptions}
        depth={depth === 'l2' ? 'levels' : 'full'}
      />
    )
  }
  if (kind === 'requirement') {
    return (
      <RubricRequirementCard
        {...chrome}
        onUpdate={onUpdate}
        depth={depth === 'l2' ? 'advanced' : 'full'}
      />
    )
  }
  return (
    <RubricDimensionCard
      {...chrome}
      onUpdate={onUpdate}
      depth={depth === 'l2' ? 'advanced' : 'full'}
    />
  )
}

export function RubricCriteriaEditor({
  criteria,
  onChange,
  validationMessage,
  skillOptions = [],
  sectionId,
  kinds,
  variant = 'settings',
  depth = 'full',
  focusCriterionKey,
}: RubricCriteriaEditorProps) {
  const rowErrors = rowErrorsFor(criteria)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const groups = useMemo(
    () =>
      GROUPS.filter((group) => !kinds || kinds.includes(group.kind)).map((group) => {
        const entries = criteria
          .map((row, i) => ({ row, i, id: `${row.key}::${i}` }))
          .filter(({ row }) => row.kind === group.kind)
        return { ...group, entries }
      }),
    [criteria, kinds],
  )

  function updateRow(globalIndex: number, patch: Partial<RubricCriterion>) {
    onChange(criteria.map((row, i) => (i === globalIndex ? { ...row, ...patch } : row)))
  }

  function removeRow(globalIndex: number) {
    onChange(criteria.filter((_, i) => i !== globalIndex))
  }

  function addRow(kind: RubricCriterion['kind']) {
    onChange([...criteria, newCriterionDraft(kind)])
  }

  function updateWeightForKey(key: string, weight: number, kind: string) {
    onChange(
      criteria.map((row) =>
        row.key === key && row.kind === kind ? { ...row, weight } : row,
      ),
    )
  }

  function handleDragEnd(kind: string, event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const kindIndices = criteria
      .map((row, i) => ({ row, i }))
      .filter(({ row }) => row.kind === kind)
      .map(({ i }) => i)
    const ids = kindIndices.map((i) => `${criteria[i].key}::${i}`)
    const oldPos = ids.indexOf(String(active.id))
    const newPos = ids.indexOf(String(over.id))
    if (oldPos < 0 || newPos < 0) return
    const reorderedKindIndices = arrayMove(kindIndices, oldPos, newPos)
    const next = [...criteria]
    const kindRows = reorderedKindIndices.map((i) => criteria[i])
    let cursor = 0
    for (let i = 0; i < next.length; i += 1) {
      if (next[i].kind === kind) {
        next[i] = { ...kindRows[cursor], sort_order: cursor }
        cursor += 1
      }
    }
    onChange(next)
  }

  return (
    <div id={sectionId} className={variant === 'settings' ? 'scroll-mt-24' : 'space-y-4'}>
      {validationMessage && variant === 'settings' ? (
        <SettingsRowFrame showDivider innerClassName="py-3">
          <p className="text-sm text-warning">{validationMessage}</p>
        </SettingsRowFrame>
      ) : null}
      {validationMessage && variant === 'canvas' ? (
        <p className="text-sm text-warning">{validationMessage}</p>
      ) : null}

      {groups.map((group) => {
        const total = weightTotalForKind(criteria, group.kind)
        const segments = group.entries.map(({ row }) => ({
          key: row.key,
          label: row.label || row.key,
          weight: row.weight ?? 0,
        }))
        const showWeights =
          depth === 'full'
          && group.kind !== 'requirement'
          && group.entries.some(({ row }) => scaleTypeOf(row) !== 'boolean')
        const l2Help =
          group.kind === 'competency'
            ? 'Edit performance levels and linked skills. Name, definition, and weight are on the case file.'
            : 'Stable keys used in analysis payloads. Name, definition, and weight are on the case file.'

        return (
          <section key={group.kind} className={variant === 'canvas' ? 'space-y-4' : undefined}>
            {variant === 'settings' ? (
              <SettingsSectionHeading isFirst={false}>{group.title}</SettingsSectionHeading>
            ) : (
              <div className="space-y-1">
                <h3 className="text-sm font-semibold">
                  {depth === 'l2' && group.kind === 'competency'
                    ? 'Levels & skills'
                    : depth === 'l2'
                      ? 'Advanced'
                      : group.title}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {depth === 'l2' ? l2Help : group.help}
                </p>
              </div>
            )}
            {depth === 'full' && variant === 'settings' ? (
              <SettingsRowFrame
                showDivider
                innerClassName="flex flex-wrap items-start justify-between gap-3 py-3"
              >
                <SettingsFieldDescription className="mt-0 max-w-prose">{group.help}</SettingsFieldDescription>
                <Button type="button" variant="outline" size="sm" onClick={() => addRow(group.kind)}>
                  <Plus className="mr-1 h-4 w-4" aria-hidden />
                  Add {group.title.toLowerCase().replace(/s$/, '')}
                </Button>
              </SettingsRowFrame>
            ) : null}
            {depth === 'full' && variant === 'canvas' ? (
              <div className="flex justify-end">
                <Button type="button" variant="outline" size="sm" onClick={() => addRow(group.kind)}>
                  <Plus className="mr-1 h-4 w-4" aria-hidden />
                  Add {group.title.toLowerCase().replace(/s$/, '')}
                </Button>
              </div>
            ) : null}

            {showWeights ? (
              <WeightAllocationBar
                embedded={variant === 'settings'}
                total={total}
                segments={segments}
                label={`${group.title} weights`}
                onNormalize={() => onChange(normalizeWeightsForKind(criteria, group.kind))}
                onWeightChange={(key, weight) => updateWeightForKey(key, weight, group.kind)}
              />
            ) : null}

            {group.entries.length === 0 ? (
              variant === 'settings' ? (
                <SettingsRowFrame showDivider innerClassName="py-4">
                  <p className="text-sm text-muted-foreground">No {group.title.toLowerCase()} yet.</p>
                </SettingsRowFrame>
              ) : (
                <p className="text-sm text-muted-foreground">No {group.title.toLowerCase()} yet.</p>
              )
            ) : (
              variant === 'settings' ? (
                <SettingsRowFrame showDivider innerClassName="space-y-4 py-4">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(group.kind, event)}
                  >
                    <SortableContext
                      items={group.entries.map((e) => e.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-4">
                        {group.entries.map(({ row, i, id }) => (
                          <div
                            key={id}
                            id={focusCriterionKey === row.key ? `criterion-${row.key}` : undefined}
                          >
                            <SortableKindCard
                              id={id}
                              kind={group.kind}
                              row={row}
                              errors={rowErrors[i]}
                              skillOptions={skillOptions}
                              depth={depth}
                              onUpdate={(patch) => updateRow(i, patch)}
                              onRemove={() => removeRow(i)}
                            />
                          </div>
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </SettingsRowFrame>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleDragEnd(group.kind, event)}
                >
                  <SortableContext
                    items={group.entries.map((e) => e.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-4">
                      {group.entries.map(({ row, i, id }) => (
                        <div
                          key={id}
                          id={focusCriterionKey === row.key ? `criterion-${row.key}` : undefined}
                        >
                          <SortableKindCard
                            id={id}
                            kind={group.kind}
                            row={row}
                            errors={rowErrors[i]}
                            skillOptions={skillOptions}
                            depth={depth}
                            onUpdate={(patch) => updateRow(i, patch)}
                            onRemove={() => removeRow(i)}
                          />
                        </div>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )
            )}
          </section>
        )
      })}
    </div>
  )
}
